import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { LOGIN_PATH } from "@/const";
import { trpc } from "@/lib/trpc";
import { AlertCircle, AlertTriangle, Clock, Flag, Loader2, ArrowLeft, ArrowRight } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useRoute, Link } from "wouter";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { seededShuffle } from "@shared/seededShuffle";

export default function Exam() {
  const { user, loading: authLoading } = useAuth();
  const [, params] = useRoute("/exam/:registrationId");
  const [, setLocation] = useLocation();
  const registrationId = params?.registrationId ? Number(params.registrationId) : null;

  const [attemptId, setAttemptId] = useState<number | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [flagged, setFlagged] = useState<Set<number>>(new Set());
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 受験申込情報取得
  const { data: registration } = trpc.examRegistrations.byId.useQuery(
    { id: registrationId! },
    { enabled: !!registrationId }
  );

  // 試験セッション情報取得
  const { data: session } = trpc.examSessions.byId.useQuery(
    { id: registration?.sessionId! },
    { enabled: !!registration?.sessionId }
  );

  // 試験レベル情報取得
  const { data: level } = trpc.examLevels.byId.useQuery(
    { id: session?.levelId! },
    { enabled: !!session?.levelId }
  );

  // 試験開始
  const startAttempt = trpc.examAttempts.start.useMutation({
    onSuccess: (data: { id: number; resumed?: boolean; remainingSeconds?: number; savedAnswers?: string | null }) => {
      setAttemptId(data.id);
      const fullDuration = (session?.duration || 40) * 60;
      const remaining = data.remainingSeconds ?? fullDuration;
      setTimeRemaining(remaining);
      if (data.resumed && data.savedAnswers) {
        try {
          const saved = JSON.parse(data.savedAnswers) as Record<number, string>;
          setAnswers(saved);
        } catch {}
      }
      toast.success(data.resumed ? "試験を再開しました" : "試験を開始しました");
      if (!data.resumed && remaining < fullDuration) {
        const minutes = Math.ceil(remaining / 60);
        toast.warning(`試験終了時刻が近いため、残り${minutes}分で制限されています`);
      }
    },
    onError: (error) => {
      if (error.data?.code === "FORBIDDEN") {
        setExamUnavailableMessage(error.message);
      } else if (error.data?.code === "CONFLICT") {
        toast.error(error.message || "この試験は既に受験済みです");
        setLocation("/my-page");
      } else {
        toast.error(error.message || "試験の開始に失敗しました");
        setLocation("/my-page");
      }
    },
  });

  const saveProgress = trpc.examAttempts.saveProgress.useMutation();

  // 問題取得 (attemptIdが確定してから取得することで問題セットを固定する)
  const { data: questions, isLoading: questionsLoading } = trpc.examQuestions.bySession.useQuery(
    { sessionId: registration?.sessionId!, attemptId: attemptId ?? undefined },
    {
      enabled: !!registration?.sessionId && !!attemptId,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  // 選択肢の表示順をシャッフル（attemptId + questionId で決定的に固定）
  const shuffledOptionsMap = useMemo(() => {
    if (!questions || !attemptId) return new Map<number, string[]>();
    const map = new Map<number, string[]>();
    for (const q of questions) {
      const available = (["A", "B", "C", "D"] as const).filter(
        (opt) => q[`option${opt}` as keyof typeof q]
      );
      map.set(q.id, seededShuffle(available, attemptId * 10007 + q.id));
    }
    return map;
  }, [questions, attemptId]);

  // 回答はローカル状態で管理し、提出時に一括保存

  // 試験提出
  const submitAttempt = trpc.examAttempts.submit.useMutation({
    onSuccess: () => {
      toast.success("試験を提出しました");
      setLocation(`/result/${attemptId}`);
    },
    onError: (error) => {
      toast.error(error.message || "提出に失敗しました");
      setIsSubmitting(false);
    },
  });

  const [examUnavailableMessage, setExamUnavailableMessage] = useState<string | null>(null);
  const [startAttemptCalled, setStartAttemptCalled] = useState(false);
  const [timerWarningShown, setTimerWarningShown] = useState(false);
  const [showAntiCheatDialog, setShowAntiCheatDialog] = useState(false);
  const [antiCheatAgreed, setAntiCheatAgreed] = useState(false);
  const [tabLeaveCount, setTabLeaveCount] = useState(0);
  const tabLeaveCountRef = useRef(0);
  const auditLogMutation = trpc.auditLogs.log.useMutation();

  // 試験開始処理（同意ダイアログ表示 → 同意後に開始）
  useEffect(() => {
    if (user && registrationId && !attemptId && registration && registration.sessionId && !examUnavailableMessage && !startAttemptCalled) {
      setShowAntiCheatDialog(true);
    }
  }, [user, registrationId, registration, examUnavailableMessage, startAttemptCalled, attemptId]);

  const handleAntiCheatAgree = () => {
    setAntiCheatAgreed(true);
    setShowAntiCheatDialog(false);
    if (registrationId && registration?.sessionId) {
      setStartAttemptCalled(true);
      startAttempt.mutate({
        registrationId,
        sessionId: registration.sessionId,
      });
    }
  };

  // タイマー
  useEffect(() => {
    if (!attemptId || timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          handleSubmit();
          return 0;
        }
        if (prev === 300 && !timerWarningShown) {
          setTimerWarningShown(true);
          toast.warning("残り5分です！");
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [attemptId, timeRemaining, timerWarningShown]);

  // 回答の自動保存（回答変更時にデバウンスして保存）
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleSave = useCallback(() => {
    if (!attemptId) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const currentAnswers = answersRef.current;
      if (Object.keys(currentAnswers).length > 0) {
        saveProgress.mutate({ attemptId, answers: JSON.stringify(currentAnswers) });
      }
    }, 2000);
  }, [attemptId]);

  // ページ離脱時に即座保存
  useEffect(() => {
    if (!attemptId) return;
    const handleBeforeUnload = () => {
      const currentAnswers = answersRef.current;
      if (Object.keys(currentAnswers).length > 0) {
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveProgress.mutate({ attemptId, answers: JSON.stringify(currentAnswers) });
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [attemptId]);

  const lastAuditRef = useRef(0);
  const handleSubmitRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (!attemptId) return;
    const throttledLog = (eventType: string, detail: string) => {
      const now = Date.now();
      if (now - lastAuditRef.current < 2000) return;
      lastAuditRef.current = now;
      auditLogMutation.mutate({ attemptId, eventType, detail });
    };

    const handleTabLeave = () => {
      const newCount = tabLeaveCountRef.current + 1;
      tabLeaveCountRef.current = newCount;
      setTabLeaveCount(newCount);

      if (newCount >= 3) {
        throttledLog("auto_submit_tab_leave", `離脱${newCount}回で自動提出`);
        toast.error("3回以上の離脱が検知されたため、試験を自動提出します。");
        setTimeout(() => handleSubmitRef.current(), 500);
      } else {
        toast.warning(`試験画面から離れました（${newCount}回目/3回）`, {
          description: "3回以上の離脱で試験は自動提出されます。",
          duration: 5000,
        });
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        throttledLog("tab_hidden", "タブが非表示になりました");
        handleTabLeave();
      } else {
        throttledLog("tab_visible", "タブが表示に戻りました");
      }
    };
    const handleBlur = () => {
      throttledLog("window_blur", "ウィンドウからフォーカスが外れました");
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
    };
  }, [attemptId]);

  // フラグ切り替え
  const toggleFlag = (questionId: number) => {
    setFlagged((prev) => {
      const next = new Set(prev);
      if (next.has(questionId)) {
        next.delete(questionId);
      } else {
        next.add(questionId);
      }
      return next;
    });
  };

  // カンニング防止
  useEffect(() => {
    if (!attemptId) return;
    const preventCopy = (e: ClipboardEvent) => { e.preventDefault(); };
    const preventContextMenu = (e: MouseEvent) => { e.preventDefault(); };
    const preventKeyboard = (e: KeyboardEvent) => {
      // コピー・全選択・印刷・保存の防止
      if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'a' || e.key === 'p' || e.key === 'P' || e.key === 's' || e.key === 'S')) {
        e.preventDefault();
      }
      // F12（DevTools）
      if (e.key === 'PrintScreen' || e.key === 'F12') {
        e.preventDefault();
      }
      // Ctrl+Shift+I (DevTools), Ctrl+Shift+J (Console), Ctrl+Shift+C (Elements)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'I' || e.key === 'i' || e.key === 'J' || e.key === 'j' || e.key === 'C' || e.key === 'c')) {
        e.preventDefault();
      }
      // Ctrl+U (View Source)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'u' || e.key === 'U')) {
        e.preventDefault();
      }
    };
    const preventDrag = (e: DragEvent) => { e.preventDefault(); };
    document.addEventListener('copy', preventCopy);
    document.addEventListener('cut', preventCopy);
    document.addEventListener('contextmenu', preventContextMenu);
    document.addEventListener('keydown', preventKeyboard);
    document.addEventListener('dragstart', preventDrag);
    return () => {
      document.removeEventListener('copy', preventCopy);
      document.removeEventListener('cut', preventCopy);
      document.removeEventListener('contextmenu', preventContextMenu);
      document.removeEventListener('keydown', preventKeyboard);
      document.removeEventListener('dragstart', preventDrag);
    };
  }, [attemptId]);

  // 回答変更時の処理
  const handleAnswerChange = (questionId: number, selectedOption: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: selectedOption }));
    scheduleSave();
  };

  const handleSubmit = () => {
    if (!attemptId || !questions) return;
    
    setIsSubmitting(true);
    
    // answersオブジェクトを配列形式に変換（回答済みの問題のみ）
    const answersArray = questions
      .filter(q => answers[q.id])
      .map(q => ({
        questionId: q.id,
        selectedAnswer: answers[q.id] as "A" | "B" | "C" | "D",
      }));
    
    submitAttempt.mutate({ 
      attemptId,
      answers: answersArray 
    });
  };

  handleSubmitRef.current = handleSubmit;

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>ログインが必要です</CardTitle>
            <CardDescription>
              試験を受けるには、ログインしてください。
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link href={LOGIN_PATH}>
              <Button className="w-full">ログイン</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (examUnavailableMessage) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-6 h-6 text-red-500" />
              受験できません
            </CardTitle>
            <CardDescription>
              {examUnavailableMessage}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {session && (
              <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-4 space-y-1">
                {session.examDate && <p>試験日: {session.examDate}</p>}
                {session.examStartTime && session.examEndTime && (
                  <p>受験可能時間: {session.examStartTime} 〜 {session.examEndTime}</p>
                )}
              </div>
            )}
            <Link href="/my-page">
              <Button className="w-full">マイページに戻る</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (questionsLoading || !questions || !session || !level) {
    return (
      <>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
        {/* カンニング対策同意ダイアログ — ポータル経由で表示 */}
        <AlertDialog open={showAntiCheatDialog}>
          <AlertDialogContent className="max-w-[calc(100%-2rem)] sm:max-w-lg">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-lg sm:text-xl">
                <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-500 flex-shrink-0" />
                試験中の注意事項
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3 text-left">
                  <ul className="space-y-2 text-xs sm:text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 font-bold mt-0.5 flex-shrink-0">*</span>
                      <span>試験中にブラウザのタブを切り替えたり、他のウィンドウに移動した場合、離脱として記録されます</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 font-bold mt-0.5 flex-shrink-0">*</span>
                      <span>3回以上の離脱が検知された場合、試験は自動的に提出されます</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 font-bold mt-0.5 flex-shrink-0">*</span>
                      <span>問題文のコピー・スクリーンショットは禁止されています</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 font-bold mt-0.5 flex-shrink-0">*</span>
                      <span>開発者ツールの使用は検知・記録されます</span>
                    </li>
                  </ul>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
              <AlertDialogCancel onClick={() => setLocation("/my-page")} className="w-full sm:w-auto">
                マイページに戻る
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleAntiCheatAgree} className="w-full sm:w-auto">
                同意して試験を開始
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;
  const answeredCount = Object.keys(answers).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50" style={{ userSelect: "none", WebkitUserSelect: "none" }}>
      {/* ヘッダー */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-purple-100 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <div>
              <h1 className="text-lg sm:text-xl font-bold">{session.displayName || level.displayName} 試験</h1>
              <p className="text-sm text-gray-600">
                問題 {currentQuestionIndex + 1} / {questions.length}
              </p>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto">
              <div className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-all ${
                timeRemaining < 60 ? "bg-red-200 text-red-800 animate-pulse shadow-lg shadow-red-200" :
                timeRemaining < 300 ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"
              }`}>
                {timeRemaining < 300 ? <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5" /> : <Clock className="w-4 h-4 sm:w-5 sm:h-5" />}
                <span className="font-mono font-bold text-base sm:text-lg">{formatTime(timeRemaining)}</span>
              </div>
              <Button
                variant="destructive"
                size="sm"
                className="sm:size-default"
                onClick={() => setShowSubmitDialog(true)}
                disabled={isSubmitting}
              >
                試験を提出
              </Button>
            </div>
          </div>
          <div className="mt-4">
            <Progress value={progress} className="h-2" />
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* 問題カード */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-2xl">問題 {currentQuestionIndex + 1}</CardTitle>
                <Button
                  variant={flagged.has(currentQuestion.id) ? "default" : "outline"}
                  size="sm"
                  className={flagged.has(currentQuestion.id) ? "bg-orange-500 hover:bg-orange-600 text-white" : "text-orange-500 border-orange-300 hover:bg-orange-50"}
                  onClick={() => toggleFlag(currentQuestion.id)}
                >
                  <Flag className="w-4 h-4 mr-1" />
                  {flagged.has(currentQuestion.id) ? "あとで見直す" : "あとで見直す"}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-lg whitespace-pre-wrap">{currentQuestion.questionText}</div>

              <RadioGroup
                value={answers[currentQuestion.id] || ""}
                onValueChange={(value) => handleAnswerChange(currentQuestion.id, value)}
              >
                <div className="space-y-3">
                  {(shuffledOptionsMap.get(currentQuestion.id) || ["A", "B", "C", "D"]).map((originalOption, idx) => {
                    const optionKey = `option${originalOption}` as keyof typeof currentQuestion;
                    const optionText = currentQuestion[optionKey];
                    const displayLabel = String.fromCharCode(65 + idx); // A, B, C, D（表示用）

                    if (!optionText) return null;

                    return (
                      <div
                        key={originalOption}
                        className={`flex items-center space-x-3 border rounded-lg p-4 cursor-pointer transition-colors ${
                          answers[currentQuestion.id] === originalOption
                            ? "border-indigo-500 bg-indigo-50"
                            : "border-gray-200 hover:border-gray-400"
                        }`}
                      >
                        <RadioGroupItem value={originalOption} id={`option-${originalOption}`} />
                        <Label
                          htmlFor={`option-${originalOption}`}
                          className="flex-1 cursor-pointer"
                        >
                          <span className="font-semibold mr-2">{displayLabel}.</span>
                          {optionText}
                        </Label>
                      </div>
                    );
                  })}
                </div>
              </RadioGroup>
            </CardContent>
          </Card>

          {/* ナビゲーション */}
          <div className="flex justify-between items-center">
            <Button
              variant="outline"
              onClick={() => setCurrentQuestionIndex((prev) => Math.max(0, prev - 1))}
              disabled={currentQuestionIndex === 0}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              前の問題
            </Button>

            <div className="text-sm text-gray-600">
              回答済み: {answeredCount} / {questions.length}
            </div>

            <Button
              onClick={() => setCurrentQuestionIndex((prev) => Math.min(questions.length - 1, prev + 1))}
              disabled={currentQuestionIndex === questions.length - 1}
            >
              次の問題
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {/* 問題一覧 */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">問題一覧</CardTitle>
                {flagged.size > 0 && (
                  <span className="text-sm text-orange-600 flex items-center gap-1">
                    <Flag className="w-3 h-3" />
                    見直し: {flagged.size}問
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mb-3">
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded border bg-white inline-block" /> 未回答</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-100 border border-green-300 inline-block" /> 回答済</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-primary inline-block" /> 現在の問題</span>
                <span className="flex items-center gap-1"><Flag className="w-3 h-3 text-orange-500" /> 見直し</span>
              </div>
              <div className="grid grid-cols-5 md:grid-cols-10 gap-2">
                {questions.map((q, index) => {
                  const isActive = currentQuestionIndex === index;
                  const isAnswered = !!answers[q.id];
                  const isFlagged = flagged.has(q.id);
                  return (
                    <Button
                      key={q.id}
                      variant={isActive ? "default" : isAnswered ? "secondary" : "outline"}
                      className={`w-full relative ${isFlagged ? "ring-2 ring-orange-400" : ""} ${isAnswered && !isActive ? "bg-green-100 text-green-800 border-green-300 hover:bg-green-200" : ""}`}
                      onClick={() => setCurrentQuestionIndex(index)}
                    >
                      {index + 1}
                      {isFlagged && (
                        <Flag className="w-3 h-3 text-orange-500 absolute -top-1 -right-1" />
                      )}
                    </Button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* 警告メッセージ */}
          {answeredCount < questions.length && (
            <Card className="border-yellow-300 bg-yellow-50">
              <CardContent className="flex items-start gap-3 p-4">
                <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  未回答の問題が {questions.length - answeredCount} 問あります。
                  すべての問題に回答してから提出することをお勧めします。
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* カンニング対策同意ダイアログ */}
      <AlertDialog open={showAntiCheatDialog}>
        <AlertDialogContent className="max-w-[calc(100%-2rem)] sm:max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-500 flex-shrink-0" />
              試験中の注意事項
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-left">
                <ul className="space-y-2 text-xs sm:text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 font-bold mt-0.5 flex-shrink-0">*</span>
                    <span>試験中にブラウザのタブを切り替えたり、他のウィンドウに移動した場合、離脱として記録されます</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 font-bold mt-0.5 flex-shrink-0">*</span>
                    <span>3回以上の離脱が検知された場合、試験は自動的に提出されます</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 font-bold mt-0.5 flex-shrink-0">*</span>
                    <span>問題文のコピー・スクリーンショットは禁止されています</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 font-bold mt-0.5 flex-shrink-0">*</span>
                    <span>開発者ツールの使用は検知・記録されます</span>
                  </li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
            <AlertDialogCancel onClick={() => setLocation("/my-page")} className="w-full sm:w-auto">
              マイページに戻る
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleAntiCheatAgree} className="w-full sm:w-auto">
              同意して試験を開始
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 提出確認ダイアログ */}
      <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>試験を提出しますか？</AlertDialogTitle>
            <AlertDialogDescription>
              提出すると２度と入れません。
              {answeredCount < questions.length && (
                <span className="block mt-2 text-yellow-600 font-semibold">
                  未回答の問題が {questions.length - answeredCount} 問あります。
                </span>
              )}
              {flagged.size > 0 && (
                <span className="block mt-2 text-orange-600 font-semibold">
                  見直しフラグが {flagged.size} 問に付いています。
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>キャンセル</AlertDialogCancel>
            <AlertDialogAction onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? "提出中..." : "提出する"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
